import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Link
} from '@mui/material';
import { 
  Save as SaveIcon, 
  ArrowBack as ArrowBackIcon, 
  Edit as EditIcon, 
  Refresh as RefreshIcon, 
  Launch as LaunchIcon,
  LinkedIn as LinkedInIcon,
  Facebook as FacebookIcon
} from '@mui/icons-material';
import { companyService } from '../services/companyService';
import { contactService } from '../services/contactService';
import { useCurrentUser } from '../hooks/useCurrentUser';
import UserDisplay from '../components/UserDisplay';
import ContactSelector from '../components/ContactSelector';
import AssociatedOpportunities from '../components/AssociatedOpportunities';
import OpportunityMapSection from '../components/OpportunityMapSection';
import { logError, formatErrorMessage } from '../utils/errorLogger';

const Company = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [websiteDialogOpen, setWebsiteDialogOpen] = useState(false);
  const [websiteValue, setWebsiteValue] = useState('');
  const [companyContacts, setCompanyContacts] = useState([]);
  const [removedContactIds, setRemovedContactIds] = useState(new Set());
  const [addedContacts, setAddedContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const isEditing = companyId && companyId !== 'new';

  const loadCompanyContacts = async (companyId) => {
    setLoadingContacts(true);
    try {
      const contacts = await contactService.getContactsByCompany(companyId);
      // Transform contacts to ensure consistent property names
      const transformedContacts = contacts.map(contact => ({
        contactId: contact.id || contact.Id || contact.contactId,
        firstName: contact.firstName || contact.FirstName,
        lastName: contact.lastName || contact.LastName,
        email: contact.email || contact.Email,
        title: contact.title || contact.Title,
        mainPhone: contact.mainPhone || contact.MainPhone,
        companyId: contact.companyId || contact.CompanyId
      }));
      setCompanyContacts(transformedContacts);
      // Reset temporary states when loading new contacts
      setRemovedContactIds(new Set());
      setAddedContacts([]);
    } catch (err) {
      console.error('Error loading company contacts:', err);
    } finally {
      setLoadingContacts(false);
    }
  };

  const loadCompany = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      companyService.validateConfiguration();
      const companyData = await companyService.getCompany(companyId);
      if (companyData) {
        setCompany(companyData);
        await loadCompanyContacts(companyId);
      } else {
        setError('Company not found');
      }
    } catch (err) {
      logError('Company', 'loadCompany', err, { companyId });
      setError(formatErrorMessage(err, 'Failed to load company details'));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (isEditing) {
      loadCompany();
    } else {
      setCompany(companyService.createEmptyCompany());
      setLoading(false);
    }
  }, [companyId, isEditing, loadCompany]);

  // Note: Auto-refresh removed from individual entity pages to prevent data loss
  // Manual refresh is still available via the refresh button

  const handleRemoveContact = (contactId) => {
    // Just mark the contact as removed in temporary state
    setRemovedContactIds(prev => new Set([...prev, contactId]));
    setSuccess('Contact marked for removal - changes will be saved when you click Save');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setCompany(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!company.companyName?.trim()) {
      errors.companyName = 'Company Name is required';
    }

    if (company.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(company.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (company.website && !/^https?:\/\/.+/.test(company.website)) {
      errors.website = 'Please enter a valid website URL (starting with http:// or https://)';
    }

    // LinkedIn Profile validation
    if (company.linkedInProfile && !company.linkedInProfile.includes('linkedin.com/')) {
      errors.linkedInProfile = 'Please enter a valid LinkedIn URL (e.g., https://linkedin.com/company/your-company)';
    }

    // Facebook Page validation  
    if (company.facebookPage && !company.facebookPage.includes('facebook.com/')) {
      errors.facebookPage = 'Please enter a valid Facebook URL (e.g., https://facebook.com/your-company)';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Validate configuration first
      companyService.validateConfiguration();
      const currentUserId = currentUser.email || 'unknown';
      
      // First save the company
      await companyService.saveCompany(company, currentUserId);

      // Then handle contact changes
      const savePromises = [];

      // Handle removed contacts
      for (const contactId of removedContactIds) {
        const contact = companyContacts.find(c => c.contactId === contactId);
        if (contact) {
          const updatedContact = {
            ...contact,
            id: contact.contactId,
            companyId: null
          };
          savePromises.push(contactService.saveContact(updatedContact, currentUser.email));
        }
      }

      // Handle added contacts
      for (const contact of addedContacts) {
        const updatedContact = {
          ...contact,
          id: contact.contactId,
          companyId: company.companyId
        };
        savePromises.push(contactService.saveContact(updatedContact, currentUser.email));
      }

      // Wait for all contact updates to complete
      await Promise.all(savePromises);
      
      setSuccess(isEditing ? 'Company updated successfully' : 'Company created successfully');
      
      // Navigate back to companies list after a short delay
      setTimeout(() => {
        navigate('/companies');
      }, 1500);
    } catch (err) {
      logError('Company', 'handleSave', err, { company, currentUserEmail: currentUser.email });
      setError(`Failed to save company: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/companies');
  };

  const handleWebsiteEdit = () => {
    setWebsiteValue(company.website);
    setWebsiteDialogOpen(true);
  };

  const handleWebsiteSave = () => {
    setCompany(prev => ({ ...prev, website: websiteValue }));
    setWebsiteDialogOpen(false);
    // Clear validation error if it exists
    if (validationErrors.website) {
      setValidationErrors(prev => ({ ...prev, website: '' }));
    }
  };

  const handleWebsiteCancel = () => {
    setWebsiteDialogOpen(false);
    setWebsiteValue('');
  };

  const handleGoogleMapsClick = () => {
    const addressParts = [];
    if (company.street1) addressParts.push(company.street1);
    if (company.street2) addressParts.push(company.street2);
    if (company.city) addressParts.push(company.city);
    if (company.province) addressParts.push(company.province);
    if (company.country) addressParts.push(company.country);
    if (company.postalCode) addressParts.push(company.postalCode);
    
    const fullAddress = addressParts.join(', ');
    if (fullAddress) {
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
      window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Get the current list of contacts (including temporary changes)
  const getCurrentContacts = useCallback(() => {
    return [
      ...companyContacts.filter(c => !removedContactIds.has(c.contactId)),
      ...addedContacts
    ];
  }, [companyContacts, removedContactIds, addedContacts]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!company) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6" color="text.secondary">
          Company data not available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', pr: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Back to Companies
          </Button>
          <Typography variant="h4">
            {isEditing ? 'Edit Company' : 'Create Company'}
          </Typography>
        </Box>
        
        {/* Refresh Button - Only show for existing companies */}
        {isEditing && (
          <IconButton
            onClick={() => loadCompany()}
            disabled={loading}
            title="Refresh data"
            sx={{ color: 'primary.main' }}
          >
            <RefreshIcon />
          </IconButton>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper elevation={3} sx={{ p: 3 }}>
        <Grid container spacing={isMobile ? 2 : 3}>
          {/* Company Name */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Company Name"
              value={company.companyName}
              onChange={handleInputChange('companyName')}
              error={!!validationErrors.companyName}
              helperText={validationErrors.companyName}
              required
            />
          </Grid>

          {/* Website */}
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Website
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {company.website ? (
                  <Link 
                    href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      flexGrow: 1,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    {company.website}
                  </Link>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                    No website specified
                  </Typography>
                )}
                <IconButton 
                  size="small" 
                  onClick={handleWebsiteEdit}
                  title="Edit Website"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
              {validationErrors.website && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {validationErrors.website}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Email */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={company.email}
              onChange={handleInputChange('email')}
              error={!!validationErrors.email}
              helperText={validationErrors.email}
            />
          </Grid>

          {/* Main Phone */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Main Phone"
              value={company.mainPhone}
              onChange={handleInputChange('mainPhone')}
            />
          </Grid>

          {/* Secondary Phone */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Secondary Phone"
              value={company.secondaryPhone}
              onChange={handleInputChange('secondaryPhone')}
            />
          </Grid>

          {/* Industry */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Industry"
              value={company.industry}
              onChange={handleInputChange('industry')}
            />
          </Grid>

          {/* LinkedIn Profile */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                label="LinkedIn Profile"
                value={company.linkedInProfile}
                onChange={handleInputChange('linkedInProfile')}
                error={!!validationErrors.linkedInProfile}
                helperText={validationErrors.linkedInProfile}
                placeholder="https://linkedin.com/company/your-company"
              />
              {company.linkedInProfile && (
                <IconButton
                  onClick={() => {
                    const url = company.linkedInProfile.startsWith('http') 
                      ? company.linkedInProfile 
                      : `https://${company.linkedInProfile}`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                  sx={{ 
                    color: '#0077b5',
                    '&:hover': { color: '#005885' }
                  }}
                  title="Open LinkedIn Profile"
                >
                  <LinkedInIcon />
                </IconButton>
              )}
            </Box>
          </Grid>

          {/* Facebook Page */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                label="Facebook Page"
                value={company.facebookPage}
                onChange={handleInputChange('facebookPage')}
                error={!!validationErrors.facebookPage}
                helperText={validationErrors.facebookPage}
                placeholder="https://facebook.com/your-company"
              />
              {company.facebookPage && (
                <IconButton
                  onClick={() => {
                    const url = company.facebookPage.startsWith('http') 
                      ? company.facebookPage 
                      : `https://${company.facebookPage}`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                  sx={{ 
                    color: '#1877f2',
                    '&:hover': { color: '#166fe5' }
                  }}
                  title="Open Facebook Page"
                >
                  <FacebookIcon />
                </IconButton>
              )}
            </Box>
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={company.description}
              onChange={handleInputChange('description')}
            />
          </Grid>

          {/* Address Section */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 2 }}>
              <Divider sx={{ flexGrow: 1 }}>
                <Typography variant="h6" color="text.secondary">
                  Address Information
                </Typography>
              </Divider>
            </Box>
          </Grid>

          {/* Street 1 */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Street 1"
              value={company.street1}
              onChange={handleInputChange('street1')}
            />
          </Grid>

          {/* Street 2 */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Street 2"
              value={company.street2}
              onChange={handleInputChange('street2')}
            />
          </Grid>

          {/* City */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="City"
              value={company.city}
              onChange={handleInputChange('city')}
            />
          </Grid>

          {/* Province/State */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Province/State"
              value={company.province}
              onChange={handleInputChange('province')}
            />
          </Grid>

          {/* Country */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Country"
              value={company.country}
              onChange={handleInputChange('country')}
            />
          </Grid>

          {/* Postal Code */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Postal Code/Zip Code"
              value={company.postalCode}
              onChange={handleInputChange('postalCode')}
            />
          </Grid>

          {/* Map Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }}>
              <Typography variant="h6" color="text.secondary">
                Map
              </Typography>
            </Divider>
            <OpportunityMapSection company={company} contacts={[]} />
          </Grid>

          {/* Contacts Section */}
          {isEditing && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3, mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" component="h3">
                    Contacts ({getCurrentContacts().length})
                    {(removedContactIds.size > 0 || addedContacts.length > 0) && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        (Unsaved changes)
                      </Typography>
                    )}
                  </Typography>
                  <ContactSelector 
                    companyId={company.companyId}
                    showUnassignedOnly={true}
                    onContactAdded={(contact) => {
                      // Add to temporary added contacts list
                      setAddedContacts(prev => [...prev, contact]);
                      setSuccess('Contact added - changes will be saved when you click Save');
                      setTimeout(() => setSuccess(''), 3000);
                    }}
                  />
                </Box>

                {loadingContacts ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {getCurrentContacts().map((contact) => (
                      <Paper 
                        key={contact.contactId} 
                        elevation={0} 
                        sx={{ 
                          p: 2, 
                          bgcolor: 'background.default',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle1">
                            {contact.firstName} {contact.lastName}
                          </Typography>
                          {contact.title && (
                            <Typography variant="body2" color="text.secondary">
                              {contact.title}
                            </Typography>
                          )}
                          {contact.email && (
                            <Typography variant="body2" color="text.secondary">
                              ✉️ {contact.email}
                            </Typography>
                          )}
                          {contact.mainPhone && (
                            <Typography variant="body2" color="text.secondary">
                              📞 {contact.mainPhone}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => window.open(`${window.location.origin}/contact/${contact.contactId}`, '_blank', 'noopener,noreferrer')}
                          >
                            View
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleRemoveContact(contact.contactId)}
                          >
                            Remove
                          </Button>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>
          )}

          {/* Associated Opportunities Section - Only show for existing companies */}
          {isEditing && (
            <Grid item xs={12}>
              <AssociatedOpportunities 
                companyId={company.companyId} 
                title="Associated Opportunities"
              />
            </Grid>
          )}

          {/* Audit Information */}
          {isEditing && (
            <>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Created By
                  </Typography>
                  <UserDisplay email={company.createdByEmail} size="medium" />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Created Date"
                  value={new Date(company.createdDate).toLocaleString()}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Company'}
          </Button>
        </Box>
      </Paper>

      {/* Website Edit Dialog */}
      <Dialog open={websiteDialogOpen} onClose={handleWebsiteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Website</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Website URL"
            value={websiteValue}
            onChange={(e) => setWebsiteValue(e.target.value)}
            placeholder="https://www.example.com"
            sx={{ mt: 1 }}
            helperText="Include http:// or https:// for external links"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWebsiteCancel}>Cancel</Button>
          <Button onClick={handleWebsiteSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Company; 